import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, Printer, Maximize, Paperclip, ZoomIn, Home, Info, DollarSign, Truck, ListOrdered, Loader2, AlertTriangle, Percent, Tag, X } from 'lucide-react';
import { getSignedUrlForFile } from '@/services/uploadService';
import { useAppSelector } from '@/app/hooks';
import { selectMeasurementUnit } from '@/features/preferences/preferencesSlice';
import { convertInchesToUnit, unitPrecision } from '@/utils/measurement';
import { translateMeasurementUnitLabel } from '@/types/measurement';
import { PriceRibbon } from '@/components/ui/PriceRibbon';
import { Button } from '@/components/ui/button';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { ImageZoomModal } from '@/components/order/ImageZoomModal';
import { calculateCurrentDpi } from './utils/printSizeForm';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import type { PrintQualityWarning } from '@/services/orderService';
import { getPrintQualityWarningLabel } from './utils/printQualityWarning';

const DISPLAYABLE_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/avif',
]);

export interface OrderImagePreviewProps {
  readonly imageId?: string;
  readonly imageUrl?: string;
  readonly originalWidthPx?: number;
  readonly originalHeightPx?: number;
  readonly previewBox?: number;
  readonly alt: string;
  readonly magnifyLabel: string;
  readonly zoomLabel: string;
  readonly placeholderLabel: string;
  readonly downloadLabel?: string;
  readonly showDownload?: boolean;
  readonly className?: string;
  readonly priceLabel?: string;
  readonly priceRibbonPosition?: {
    top?: string;
    right?: string;
  };
  readonly widthIn?: number | null;
  readonly heightIn?: number | null;
  readonly marginIn?: number | null;
  readonly galleryWrapIn?: number | null;
  readonly warningLabel?: string | null;
}

export const OrderImagePreview: React.FC<OrderImagePreviewProps> = ({
  imageId,
  imageUrl,
  originalWidthPx,
  originalHeightPx,
  previewBox = 200,
  alt,
  magnifyLabel,
  zoomLabel,
  placeholderLabel,
  downloadLabel,
  showDownload = true,
  className,
  priceLabel,
  priceRibbonPosition,
  widthIn,
  heightIn,
  marginIn,
  galleryWrapIn,
  warningLabel,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [fullUrl, setFullUrl] = useState<string | undefined>(undefined);
  const [mimeType, setMimeType] = useState<string | undefined>(undefined);
  const [isZoomOpen, setIsZoomOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!imageId) {
        setPreviewUrl(undefined);
        setFullUrl(undefined);
        setMimeType(undefined);
        return;
      }
      try {
        const { signedUrl, thumbnailSignedUrl, mimeType: remoteMime } = await getSignedUrlForFile(imageId);
        if (cancelled) {
          return;
        }
        setPreviewUrl(thumbnailSignedUrl ?? signedUrl);
        setFullUrl(signedUrl);
        setMimeType(remoteMime);
      } catch (error) {
        if (cancelled) {
          return;
        }
        logging.error('Failed to get signed URL', { error });
        setPreviewUrl(undefined);
        setFullUrl(undefined);
        setMimeType(undefined);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [imageId]);

  const containerStyle = { width: previewBox, height: previewBox } as const;

  let borderW = previewBox;
  let borderH = previewBox;
  if (originalWidthPx && originalHeightPx && originalWidthPx > 0 && originalHeightPx > 0) {
    const scale = Math.min(previewBox / originalWidthPx, previewBox / originalHeightPx);
    borderW = Math.max(1, Math.round(originalWidthPx * scale));
    borderH = Math.max(1, Math.round(originalHeightPx * scale));
  }

  const normalizedMime = mimeType?.toLowerCase();
  const canRenderOriginal = normalizedMime ? DISPLAYABLE_MIME_TYPES.has(normalizedMime) : true;
  const zoomSrc = canRenderOriginal
    ? fullUrl ?? imageUrl ?? previewUrl ?? undefined
    : previewUrl ?? imageUrl ?? undefined;

  const basePreviewSrc = previewUrl ?? imageUrl ?? fullUrl ?? undefined;

  if (!basePreviewSrc) {
    return (
      <div
        className={`relative mb-4 rounded-lg flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 p-8 text-gray-500 dark:text-gray-400 mx-auto ${className ?? ''}`}
        style={containerStyle}
      >
        {warningLabel ? (
          <AlertTriangle
            className="absolute left-2 top-2 h-5 w-5 rounded-sm bg-amber-50 p-0.5 text-amber-600 ring-1 ring-amber-300"
            aria-label={warningLabel}
          />
        ) : null}
        <ImageIcon className="w-16 h-16 mb-2" />
        <p>{placeholderLabel}</p>
      </div>
    );
  }

  return (
    <figure
      className={`mb-4 rounded-lg bg-gray-100 dark:bg-gray-700 mx-auto flex items-center justify-center relative group ${className ?? ''}`}
      style={containerStyle}
    >
      {priceLabel ? (
        <PriceRibbon
          label={priceLabel}
          offsetTop={priceRibbonPosition?.top}
          offsetRight={priceRibbonPosition?.right}
          aria-label={`Price ${priceLabel}`}
        />
      ) : null}
      {warningLabel ? (
        <AlertTriangle
          className="absolute left-2 top-2 z-10 h-5 w-5 rounded-sm bg-amber-50 p-0.5 text-amber-600 ring-1 ring-amber-300"
          aria-label={warningLabel}
        />
      ) : null}
      <div
        className="border border-dashed border-gray-400 dark:border-gray-500 flex items-center justify-center overflow-hidden"
        style={{ width: borderW, height: borderH }}
        aria-label={magnifyLabel}
      >
        <img src={basePreviewSrc} alt={alt} className="w-full h-full object-contain" />
      </div>
      <button
        type="button"
        className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-black/60 text-white px-2 py-1 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
        aria-label={magnifyLabel}
        onClick={() => {
          analytics.track('button: ui.order_summary_image_zoom_open', { imageId: imageId ?? null });
          setIsZoomOpen(true);
        }}
      >
        <ZoomIn className="w-4 h-4" />
        {zoomLabel}
      </button>
      <ImageZoomModal
        isOpen={isZoomOpen}
        onClose={() => {
          analytics.track('ImageZoomModal: ui.order_summary_image_zoom_close', { imageId: imageId ?? null });
          setIsZoomOpen(false);
        }}
        imageUrl={zoomSrc ?? ''}
        widthIn={typeof widthIn === 'number' ? widthIn : widthIn ?? null}
        heightIn={typeof heightIn === 'number' ? heightIn : heightIn ?? null}
        marginIn={typeof marginIn === 'number' ? marginIn : marginIn ?? null}
        galleryWrapIn={typeof galleryWrapIn === 'number' ? galleryWrapIn : galleryWrapIn ?? null}
        originalWidthPx={originalWidthPx ?? null}
        originalHeightPx={originalHeightPx ?? null}
        downloadUrl={showDownload ? fullUrl ?? null : null}
        downloadLabel={downloadLabel ?? null}
        showDownload={showDownload}
      />
    </figure>
  );
};

interface OrderSummaryProps {
  paperType?: string;
  printer?: string;
  width?: number;
  height?: number;
  imageId?: string;
  imageUrl?: string;
  originalWidthPx?: number;
  originalHeightPx?: number;
  widthIn?: number;
  heightIn?: number;
  marginIn?: number | null;
  galleryWrapIn?: number | null;
  shippingAddressText?: string;
  isPaperConfigured?: boolean;
  isPrinterConfigured?: boolean;
  isPrintSizeConfigured?: boolean;
  hasShippingAddress?: boolean;
  priceLabel?: string | null;
  quantityLabel?: string | null;
  quantityCostLabel?: string | null;
  printQualityWarning?: PrintQualityWarning | null;
  activeItemLabel?: string;
  orderTotalLabel?: string | null;
  orderTotalHelper?: string | null;
  itemsSubtotalLabel?: string | null;
  itemsSubtotalHelper?: string | null;
  userDiscountLabel?: string | null;
  userDiscountHelper?: string | null;
  promoCodeControl?: {
    isVisible: boolean;
    code: string;
    isApplying: boolean;
    errorMessage?: string | null;
    appliedCodes: Array<{
      code: string;
      discountLabel: string;
    }>;
    onShow: () => void;
    onCodeChange: (value: string) => void;
    onApply: () => void;
    onRemove: (code: string) => void;
  } | null;
  shippingCostLabel?: string | null;
  shippingCostSummary?: React.ReactNode;
  shippingCostHelper?: string | null;
  shippingOptionsToggle?: {
    isVisible: boolean;
    onToggle: () => void;
    showWarning?: boolean;
  };
  shippingOptionsContent?: React.ReactNode;
  paymentSection?: PaymentSectionProps | null;
}

interface PaymentSectionProps {
  readonly orderId?: string | null;
  readonly balanceMessage: string;
  readonly shortfallMessage?: string | null;
  readonly helperText?: string | null;
  readonly summaryMessage?: string | null;
  readonly statusLabel?: string | null;
  readonly errorMessage?: string | null;
  readonly refreshAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
  readonly topUpAction?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
  };
  readonly confirmAction: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    highlight?: boolean;
  };
  readonly currencyControl?: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: ReadonlyArray<{ value: string; label: string }>;
    helperText?: string | null;
    loading?: boolean;
  };
  readonly exchangeRateInfo?: string | null;
}

interface DetailItemProps {
  icon: React.ElementType;
  label: React.ReactNode;
  labelHelper?: React.ReactNode;
  value?: string | null;
  valueHelper?: React.ReactNode;
  helperText?: string | null;
  footer?: React.ReactNode;
  emptyValue?: React.ReactNode;
  placeholder: string;
  showDivider?: boolean;
}

export const DetailItem: React.FC<DetailItemProps> = ({
  icon: Icon,
  label,
  labelHelper,
  value,
  valueHelper,
  helperText,
  footer,
  emptyValue,
  placeholder,
  showDivider = true,
}) => {
  const hasValue = Boolean(value && value.trim().length > 0);

  return (
    <div className={`flex items-start justify-between py-2 ${showDivider ? 'border-b border-gray-200 dark:border-gray-700' : ''}`}>
      <div className="flex items-start">
        <Icon className="w-4 h-4 mr-3 mt-0.5 text-gray-500 dark:text-gray-400" />
        <div className="flex flex-col">
          <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
          {labelHelper ? (
            <div className="mt-1 text-xs text-muted-foreground">
              {labelHelper}
            </div>
          ) : null}
        </div>
      </div>
      <div className="flex flex-col items-end gap-1 text-right max-w-[65%]">
        {hasValue ? (
          <span className="text-sm font-semibold text-gray-800 dark:text-white break-words">
            {value}
          </span>
        ) : emptyValue ? (
          <div className="w-full">{emptyValue}</div>
        ) : (
          <span className="text-sm font-semibold text-gray-800 dark:text-white break-words">
            {placeholder}
          </span>
        )}
        {valueHelper ? (
          <div className="mt-1">{valueHelper}</div>
        ) : null}
        {hasValue || emptyValue
          ? null
          : helperText
            ? (
              <span className="inline-flex items-start gap-1 text-xs text-muted-foreground italic">
                <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>{helperText}</span>
              </span>
            )
            : null}
        {footer ? (
          <div className="mt-1">{footer}</div>
        ) : null}
      </div>
    </div>
  );
};

const OrderSummary: React.FC<OrderSummaryProps> = ({
  paperType,
  printer,
  width,
  height,
  imageId,
  imageUrl,
  originalWidthPx,
  originalHeightPx,
  widthIn,
  heightIn,
  marginIn,
  galleryWrapIn,
  shippingAddressText,
  isPaperConfigured = false,
  isPrinterConfigured = false,
  isPrintSizeConfigured = false,
  hasShippingAddress = false,
  priceLabel,
  quantityLabel,
  quantityCostLabel,
  printQualityWarning,
  activeItemLabel,
  orderTotalLabel,
  orderTotalHelper,
  itemsSubtotalLabel,
  itemsSubtotalHelper,
  userDiscountLabel,
  userDiscountHelper,
  promoCodeControl,
  shippingCostLabel,
  shippingCostSummary,
  shippingCostHelper,
  shippingOptionsToggle,
  shippingOptionsContent,
  paymentSection,
}) => {
  const { t } = useTranslation();
  const [shouldPulseConfirmAction, setShouldPulseConfirmAction] = useState(false);
  const measurementUnit = useAppSelector(selectMeasurementUnit);
  const precision = unitPrecision(measurementUnit);

  useEffect(() => {
    if (!paymentSection?.confirmAction.highlight) {
      setShouldPulseConfirmAction(false);
      return undefined;
    }

    setShouldPulseConfirmAction(true);
    const timeoutId = window.setTimeout(() => {
      setShouldPulseConfirmAction(false);
    }, 6000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [paymentSection?.confirmAction.highlight]);

  const unitLabel = useMemo(() => translateMeasurementUnitLabel(t, measurementUnit), [measurementUnit, t]);

  const formatDimensionPair = useCallback((widthInches?: number, heightInches?: number) => {
    if (
      typeof widthInches !== 'number'
      || typeof heightInches !== 'number'
      || widthInches <= 0
      || heightInches <= 0
    ) {
      return null;
    }
    const formattedWidth = convertInchesToUnit(widthInches, measurementUnit).toFixed(precision);
    const formattedHeight = convertInchesToUnit(heightInches, measurementUnit).toFixed(precision);
    return `${formattedWidth}${unitLabel} × ${formattedHeight}${unitLabel}`;
  }, [measurementUnit, precision, unitLabel]);

  const formatSingleDimension = useCallback((valueInches?: number | null) => {
    if (typeof valueInches !== 'number' || valueInches <= 0) {
      return null;
    }
    return `${convertInchesToUnit(valueInches, measurementUnit).toFixed(precision)}${unitLabel}`;
  }, [measurementUnit, precision, unitLabel]);

  const placeholder = t('orderSummary.placeholder', '—');

  const paperHelper = isPaperConfigured ? null : t('orderSummary.missing.paper', 'Choose a paper in the Paper step.');
  const printerHelper = isPrinterConfigured ? null : t('orderSummary.missing.printer', 'Pick a printer in the Printer step.');
  const sizeHelper = isPrintSizeConfigured ? null : t('orderSummary.missing.size', 'Set the print size before paying.');
  const shippingHelper = hasShippingAddress ? null : t('orderSummary.missing.shipping', 'Add a shipping address to finish checkout.');

  const printSize = formatDimensionPair(width, height);
  const edgeTreatmentValue = typeof marginIn === 'number' && marginIn > 0
    ? formatSingleDimension(marginIn)
    : typeof galleryWrapIn === 'number' && galleryWrapIn > 0
      ? formatSingleDimension(galleryWrapIn)
      : null;
  const edgeTreatmentLabel = typeof marginIn === 'number' && marginIn > 0
    ? t('orderSummary.marginValue', 'Margin')
    : typeof galleryWrapIn === 'number' && galleryWrapIn > 0
      ? t('orderSummary.galleryWrapValue', 'Gallery Wrap')
      : t('orderSummary.marginGalleryWrapValue', 'Margin / Gallery Wrap');
  const widthForDpi = typeof width === 'number' && width > 0
    ? width
    : typeof widthIn === 'number' && widthIn > 0
      ? widthIn
      : null;
  const heightForDpi = typeof height === 'number' && height > 0
    ? height
    : typeof heightIn === 'number' && heightIn > 0
      ? heightIn
      : null;

  const { dpiX, dpiY } = useMemo(() => {
    if (!originalWidthPx || !originalHeightPx) {
      return { dpiX: null, dpiY: null };
    }

    return calculateCurrentDpi({
      pixelWidth: originalWidthPx ?? null,
      pixelHeight: originalHeightPx ?? null,
      widthIn: widthForDpi,
      heightIn: heightForDpi,
      marginIn: typeof marginIn === 'number' ? marginIn : null,
    });
  }, [heightForDpi, marginIn, originalHeightPx, originalWidthPx, widthForDpi]);

  const printDpi = dpiX && dpiY ? `${dpiX} × ${dpiY}` : null;
  const printQualityWarningLabel = getPrintQualityWarningLabel({
    printQualityWarning,
  }, t);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 my-4 border border-gray-200 dark:border-gray-700 max-w-5xl w-full">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{t('orderSummary.title')}</h3>
      {activeItemLabel ? (
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <Info className="h-3.5 w-3.5" aria-hidden />
          <span>{activeItemLabel}</span>
        </div>
      ) : null}
      <div className="flex flex-col gap-6 md:flex-row md:items-start">
        <div className="flex justify-center md:w-1/2 md:justify-start">
          <OrderImagePreview
            imageId={imageId}
            imageUrl={imageUrl}
            originalWidthPx={originalWidthPx ?? undefined}
            originalHeightPx={originalHeightPx ?? undefined}
            previewBox={240}
            alt={t('orderSummary.imageAlt', 'Uploaded image preview')}
            magnifyLabel={t('orderSummary.magnify', 'View details')}
            zoomLabel={t('orderSummary.zoom', 'Zoom')}
            placeholderLabel={t('orderSummary.noImage', 'No image available')}
            downloadLabel={t('orderSummary.downloadOriginal', 'Download original')}
            priceLabel={priceLabel ?? undefined}
            priceRibbonPosition={{ top: '0.75rem', right: '0.75rem' }}
            warningLabel={printQualityWarningLabel}
            className="md:mb-0"
            widthIn={widthForDpi}
            heightIn={heightForDpi}
            marginIn={typeof marginIn === 'number' ? marginIn : null}
            galleryWrapIn={typeof galleryWrapIn === 'number' ? galleryWrapIn : null}
          />
        </div>

        <div className="flex-1 md:w-1/2 md:flex-1">
          <div className="space-y-1">
            <h4 className="font-semibold text-lg text-gray-800 dark:text-gray-100 mb-2">{t('orderSummary.printDetails')}</h4>
            <DetailItem
              icon={Paperclip}
              label={t('orderSummary.paperType')}
              value={paperType}
              helperText={paperHelper}
              placeholder={placeholder}
            />
            <DetailItem
              icon={Printer}
              label={t('orderSummary.printer')}
              value={printer}
              helperText={printerHelper}
              placeholder={placeholder}
            />
            <DetailItem
              icon={Maximize}
              label={t('orderSummary.printSizeIn', 'Print size ({{unit}})', { unit: unitLabel })}
              value={printSize}
              helperText={sizeHelper}
              placeholder={placeholder}
            />
            <DetailItem
              icon={Maximize}
              label={edgeTreatmentLabel}
              value={edgeTreatmentValue}
              placeholder={placeholder}
            />
            <DetailItem
              icon={ZoomIn}
              label={t('orderSummary.printDpi', 'PPI (print size)')}
              value={printDpi}
              helperText={printQualityWarningLabel ?? undefined}
              placeholder={placeholder}
            />
            <DetailItem
              icon={ListOrdered}
              label={t('orderSummary.quantity', 'Quantity')}
              value={quantityLabel}
              placeholder={placeholder}
            />
            {quantityCostLabel ? (
              <DetailItem
                icon={DollarSign}
                label={t('orderSummary.quantityCost', 'Cost for quantity')}
                value={quantityCostLabel}
                placeholder={placeholder}
              />
            ) : null}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <h4 className="font-semibold text-lg text-gray-800 dark:text-gray-100 mb-2">
          {t('orderSummary.shippingDetails')}
        </h4>
        <DetailItem
          icon={Home}
          label={t('orderSummary.shippingAddress')}
          value={shippingAddressText}
          helperText={shippingHelper}
          placeholder={placeholder}
          showDivider={false}
        />
      </div>

      <div className="mt-6 space-y-1">
        <h4 className="font-semibold text-lg text-gray-800 dark:text-gray-100 mb-2">
          {t('orderSummary.orderTotal', 'Order total')}
        </h4>
        <DetailItem
          icon={ListOrdered}
          label={t('orderSummary.itemsSubtotal', 'Items subtotal')}
          value={itemsSubtotalLabel}
          helperText={itemsSubtotalHelper}
          placeholder={placeholder}
        />
        <DetailItem
          icon={Percent}
          label={(
            <span className="inline-flex items-center gap-1">
              <span>{t('orderSummary.userDiscount', 'Including user discount')}</span>
              <span
                title={t('orderSummary.userDiscountTooltip', 'Item prices already include this discount.')}
                aria-label={t('orderSummary.userDiscountTooltip', 'Item prices already include this discount.')}
                className="inline-flex text-gray-500 dark:text-gray-400"
              >
                <Info className="h-3.5 w-3.5" aria-hidden />
              </span>
            </span>
          )}
          value={userDiscountLabel}
          helperText={userDiscountHelper}
          placeholder={placeholder}
        />
        <DetailItem
          icon={Tag}
          label={t('orderSummary.promoCodes', 'Promo codes')}
          emptyValue={promoCodeControl ? (
            <div className="flex w-full flex-col items-end gap-2">
              <div className="flex w-full items-start justify-end gap-2">
                {promoCodeControl.appliedCodes.length > 0 ? (
                  <div className="flex min-w-0 flex-1 flex-wrap items-start justify-end gap-2">
                    {promoCodeControl.appliedCodes.map((entry) => (
                      <span
                        key={entry.code}
                        className="inline-flex h-9 max-w-full items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 text-xs font-medium text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
                      >
                        <span className="truncate">{entry.code}</span>
                        <span className="shrink-0">{entry.discountLabel}</span>
                        <button
                          type="button"
                          onClick={() => promoCodeControl.onRemove(entry.code)}
                          className="shrink-0 rounded-sm p-0.5 hover:bg-emerald-100 dark:hover:bg-emerald-900"
                          aria-label={t('orderSummary.removePromoCode', 'Remove promo code {{code}}', { code: entry.code })}
                        >
                          <X className="h-3 w-3" aria-hidden />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}
                {!promoCodeControl.isVisible ? (
                  <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={promoCodeControl.onShow}>
                    {t('orderSummary.addPromoCode', 'Add promo code')}
                  </Button>
                ) : null}
              </div>
              {promoCodeControl.isVisible ? (
                <>
                  <div className="flex w-full items-center gap-2">
                    <input
                      type="text"
                      value={promoCodeControl.code}
                      onChange={(event) => promoCodeControl.onCodeChange(event.target.value)}
                      placeholder={t('orderSummary.promoCodePlaceholder', 'Enter promo code')}
                      className="h-9 w-full rounded-md border border-gray-300 bg-white px-3 text-sm uppercase text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={promoCodeControl.onApply}
                      disabled={!promoCodeControl.code.trim() || promoCodeControl.isApplying}
                    >
                      {promoCodeControl.isApplying
                        ? t('orderSummary.promoCodeApplying', 'Applying...')
                        : t('orderSummary.promoCodeApply', 'Apply')}
                    </Button>
                  </div>
                  {promoCodeControl.errorMessage ? (
                    <span className="text-xs text-red-500">{promoCodeControl.errorMessage}</span>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : undefined}
          placeholder={placeholder}
        />
        <DetailItem
          icon={Truck}
          label={t('orderSummary.shippingCost', 'Shipment cost')}
          labelHelper={shippingCostSummary}
          value={shippingCostLabel}
          helperText={shippingCostHelper}
          placeholder={placeholder}
          footer={shippingOptionsToggle ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-green underline underline-offset-2 hover:text-brand-green/80"
              onClick={shippingOptionsToggle.onToggle}
            >
              {shippingOptionsToggle.showWarning && !shippingOptionsToggle.isVisible ? (
                <AlertTriangle
                  className="h-3.5 w-3.5 rounded-sm bg-amber-100 p-0.5 text-amber-700 ring-1 ring-amber-300"
                  aria-hidden
                />
              ) : null}
              {shippingOptionsToggle.isVisible
                ? t('orderSummary.shippingOptionsHide', 'Hide shipping options')
                : t('orderSummary.shippingOptionsShow', 'Show shipping options')}
            </button>
          ) : null}
        />
        <DetailItem
          icon={DollarSign}
          label={t('orderSummary.totalLabel', 'Total')}
          value={orderTotalLabel}
          helperText={orderTotalHelper}
          placeholder={placeholder}
        />
        {shippingOptionsContent}
        {paymentSection ? (
          <div className="mt-4 space-y-4">
            <div className="space-y-4 rounded-xl border border-green-200 bg-green-50/80 p-4">
              <div className="flex flex-col gap-1">
                <span className="text-sm font-semibold text-green-900">
                  {t('orderSummary.payAndConfirmTitle', 'Pay and confirm order')}
                </span>
                {paymentSection.helperText ? (
                  <p className="text-xs text-green-700">{paymentSection.helperText}</p>
                ) : null}
              </div>
              {paymentSection.currencyControl ? (
                <div className="space-y-2 rounded-md border border-green-200 bg-white p-3">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="liqpay-currency-select" className="text-sm font-medium text-green-900">
                      {paymentSection.currencyControl.label}
                    </Label>
                    {paymentSection.currencyControl.loading ? (
                      <Loader2 className="h-4 w-4 animate-spin text-green-700" aria-hidden />
                    ) : null}
                  </div>
                  <Select
                    value={paymentSection.currencyControl.value}
                    onValueChange={paymentSection.currencyControl.onChange}
                  >
                    <SelectTrigger id="liqpay-currency-select" className="w-full text-green-900">
                      <SelectValue placeholder={paymentSection.currencyControl.label} />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentSection.currencyControl.options.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {paymentSection.currencyControl.helperText ? (
                    <p className="text-xs text-green-700">
                      {paymentSection.currencyControl.helperText}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <div className="space-y-1 rounded-md border border-green-200 bg-white p-3">
                <p className="text-sm font-medium text-green-900">{paymentSection.balanceMessage}</p>
                {paymentSection.shortfallMessage ? (
                  <p className="text-xs text-green-700">{paymentSection.shortfallMessage}</p>
                ) : null}
              </div>
              {paymentSection.errorMessage ? (
                <p className="text-sm text-red-600" role="alert">
                  {paymentSection.errorMessage}
                </p>
              ) : null}
              {paymentSection.summaryMessage ? (
                <div className="rounded-md border border-green-200 bg-white p-3 text-sm text-green-700">
                  {paymentSection.summaryMessage}
                </div>
              ) : null}
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
                {paymentSection.refreshAction ? (
                  <Button
                    type="button"
                    className="w-full border border-brand-green/50 bg-white text-brand-green hover:bg-brand-green/10 disabled:cursor-not-allowed disabled:border-brand-green disabled:bg-brand-green disabled:text-white disabled:opacity-100 disabled:hover:bg-brand-green sm:w-auto"
                    onClick={paymentSection.refreshAction.onClick}
                    disabled={paymentSection.refreshAction.disabled}
                  >
                    {paymentSection.refreshAction.loading
                      ? t('wallet.refreshingBalance', 'Refreshing…')
                      : paymentSection.refreshAction.label}
                  </Button>
                ) : null}
                {paymentSection.topUpAction ? (
                  <Button
                    type="button"
                    className="w-full bg-brand-green text-white hover:bg-brand-green/90 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                    onClick={paymentSection.topUpAction.onClick}
                    disabled={paymentSection.topUpAction.disabled}
                  >
                    {paymentSection.topUpAction.loading
                      ? t('orderSummary.topUpProcessing', 'Processing top-up…')
                      : paymentSection.topUpAction.label}
                  </Button>
                ) : null}
                <Button
                  type="button"
                  className={
                    paymentSection.confirmAction.highlight
                      ? `w-full bg-brand-green text-white shadow-md shadow-brand-green/20 ring-2 ring-brand-green/25 transition hover:bg-brand-green/90 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-500 disabled:opacity-100 disabled:shadow-none disabled:ring-0 disabled:hover:bg-gray-200 sm:w-auto ${shouldPulseConfirmAction ? 'motion-safe:animate-pulse' : ''}`
                      : 'w-full border border-brand-green bg-white text-brand-green hover:bg-brand-green/10 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-200 disabled:text-gray-500 disabled:opacity-100 disabled:hover:bg-gray-200 sm:w-auto'
                  }
                  onClick={paymentSection.confirmAction.onClick}
                  disabled={paymentSection.confirmAction.disabled}
                >
                  {paymentSection.confirmAction.loading
                    ? t('orderSummary.confirmProcessing', 'Confirming…')
                    : paymentSection.confirmAction.label}
                </Button>
              </div>
              {paymentSection.statusLabel ? (
                <p className="text-xs text-green-800">{paymentSection.statusLabel}</p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default OrderSummary;
